import {
  startOfWeek,
  endOfWeek,
  format,
  parse,
  isSameWeek,
  addWeeks,
  subWeeks,
  isBefore,
} from "date-fns";
import { Temporal } from "temporal-polyfill";

// Week key format: "2025-W01" (ISO week number, starts on Monday)
export type WeekKey = string;

// ISO week format uses RRRR (ISO week-numbering year) and II (ISO week number)
// This is different from yyyy (calendar year) which can't be mixed with week numbers
const WEEK_KEY_FORMAT = "RRRR-'W'II";

/**
 * Get the week key for a given date.
 * Weeks start on Monday (ISO week format).
 */
export function getWeekKey(date: Date): WeekKey {
  return format(date, WEEK_KEY_FORMAT);
}

/**
 * Parse a week key into start and end dates.
 * Returns Monday 00:00:00 to Sunday 23:59:59.999
 */
export function parseWeekKey(weekKey: WeekKey): { start: Date; end: Date } {
  // Parse the week key to get a date in that week
  const dateInWeek = parse(weekKey, WEEK_KEY_FORMAT, new Date());
  const start = startOfWeek(dateInWeek, { weekStartsOn: 1 });
  const end = endOfWeek(dateInWeek, { weekStartsOn: 1 });

  return { start, end };
}

/**
 * Get the previous week key.
 */
export function getPreviousWeekKey(weekKey: WeekKey): WeekKey {
  const { start } = parseWeekKey(weekKey);
  const previousWeek = subWeeks(start, 1);
  return getWeekKey(previousWeek);
}

/**
 * Get the next week key.
 */
export function getNextWeekKey(weekKey: WeekKey): WeekKey {
  const { start } = parseWeekKey(weekKey);
  const nextWeek = addWeeks(start, 1);
  return getWeekKey(nextWeek);
}

/**
 * Check if two dates are in the same week (Monday-Sunday).
 */
export function areSameWeek(date1: Date, date2: Date): boolean {
  return isSameWeek(date1, date2, { weekStartsOn: 1 });
}

/**
 * Format a week range for display.
 * Example: "Dec 23 - Dec 29, 2024"
 */
export function formatWeekRange(weekKey: WeekKey): string {
  const { start, end } = parseWeekKey(weekKey);
  const startStr = format(start, "MMM d");
  const endStr = format(end, "MMM d, yyyy");
  return `${startStr} - ${endStr}`;
}

/**
 * Format a week range for compact display.
 * Example: "Dec 23 - 29"
 */
export function formatWeekRangeCompact(weekKey: WeekKey): string {
  const { start, end } = parseWeekKey(weekKey);
  const startStr = format(start, "MMM d");
  const endStr = format(end, "d");
  return `${startStr} - ${endStr}`;
}

/**
 * Get the current date in a specific timezone.
 * Falls back to local time if timezone is not provided.
 */
function getNowInTimezone(timezoneId?: string): Date {
  if (!timezoneId) {
    return new Date();
  }
  try {
    const now = Temporal.Now.zonedDateTimeISO(timezoneId);
    return new Date(now.epochMilliseconds);
  } catch {
    return new Date();
  }
}

/**
 * Check if a week is the current week.
 * @param timezoneId - Optional timezone to determine "current" (defaults to local)
 */
export function isCurrentWeek(weekKey: WeekKey, timezoneId?: string): boolean {
  const now = getNowInTimezone(timezoneId);
  const currentWeekKey = getWeekKey(now);
  return weekKey === currentWeekKey;
}

/**
 * Check if a week is in the past (before current week).
 * @param timezoneId - Optional timezone to determine "now" (defaults to local)
 */
export function isPastWeek(weekKey: WeekKey, timezoneId?: string): boolean {
  const { end } = parseWeekKey(weekKey);
  const now = getNowInTimezone(timezoneId);
  return isBefore(end, startOfWeek(now, { weekStartsOn: 1 }));
}

/**
 * Get the deadline text for a week's timesheet.
 * Timesheets are due by end of day Monday following the week.
 * @param timezoneId - Optional timezone to determine deadline status (defaults to local)
 */
export function getDeadlineText(weekKey: WeekKey, timezoneId?: string): string {
  const { end } = parseWeekKey(weekKey);
  const deadline = addWeeks(end, 0); // Sunday end of week
  const deadlineMonday = addWeeks(startOfWeek(end, { weekStartsOn: 1 }), 1);

  const now = getNowInTimezone(timezoneId);

  if (isBefore(now, deadline)) {
    return `Due by Monday, ${format(deadlineMonday, "MMM d")}`;
  }

  if (isBefore(now, deadlineMonday)) {
    return "Due today";
  }

  return "Overdue";
}

/**
 * Sort week keys in descending order (most recent first).
 */
export function sortWeekKeysDescending(weekKeys: WeekKey[]): WeekKey[] {
  return [...weekKeys].sort((a, b) => {
    const aStart = parseWeekKey(a).start;
    const bStart = parseWeekKey(b).start;
    return bStart.getTime() - aStart.getTime();
  });
}
